import AsyncSelect from "react-select/async";
import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";
import { useSelector } from "react-redux";

import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import PaginationComponent from "../../../components/Pagination";
import Badge from "../../../components/Badge";
import ErrorMessage from "../../../components/ErrorMessage";
import DateTimePicker from "../../../components/DateTimePicker";
import DataExporter from "../../../components/DataExporter";
import Currency, {
  ConvertCurrency,
  ConvertCurrencyToText,
} from "../../../helpers/Currency/Currency";
import { discountCodeAdminContext } from "../../../context/AdminContext/DiscountCodeAdminContext/DiscountCodeAdminContext";
import { createDiscountCodeValidation } from "../../../utils/validation/validation";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import {
  convertDate,
  numberSeparate,
  removeNumberSeparate,
} from "../../../utils";
import {
  convertDiscountCodeTypeToText,
  discountCodeTypeItems,
  exporterData,
} from "../../../utils/enum";

const DiscountCodesAdmin = () => {
  const { product, category, user } = useParams();

  const loader = useSelector((state) => state.loader);

  const {
    discountCodes,
    discountCode,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    isActive,
    setIsActive,
    type,
    setType,
    pageCount,
    itemsCount,
    setItemsCount,
    pageNumber,
    setPageNumber,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetCategories,
    handleGetProducts,
    handleGetUsers,
    handleGetDiscountCodes,
    handleCreateDiscountCode,
    handleEditDiscountCode,
    handleChangeDiscountCodeActive,
    handleDeleteDiscountCode,
    generateRandomDiscountCode,
  } = useContext(discountCodeAdminContext);

  useEffect(() => {
    handleGetDiscountCodes();
  }, [
    start,
    end,
    pageNumber,
    type,
    itemsCount,
    sort,
    product,
    category,
    user,
    isActive,
  ]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      percentage: 0,
      minPurchase: 0,
      maxDiscount: 0,
      amount: 0,
      limit: 0,
      singleUse: false,
    },
    resolver: yupResolver(createDiscountCodeValidation),
  });

  return (
    <>
      <SiteTitle title="کدهای تخفیف" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        discountCodeType={type}
        setDiscountCodeType={setType}
        setIsActive={setIsActive}
        isActive={isActive}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          endItem={
            <div className="d-flex justify-content-between align-items-center gap-2">
              {!isEmpty(discountCodes) && (
                <DataExporter
                  headers={exporterData.discountCodes.headers}
                  keys={exporterData.discountCodes.keys}
                  data={discountCodes}
                  fileName={exporterData.discountCodes.fileName}
                />
              )}
              <button
                className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                onClick={() => {
                  handleShowModal();
                  reset();
                }}
              >
                <Icon icon="ic:baseline-plus" className="text-xl" />
                افزودن کدتخفیف
              </button>
            </div>
          }
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetDiscountCodes}
        />
        <div className="card-body p-24">
          {isEmpty(discountCodes) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">عنوان</th>
                      <th scope="col">کد</th>
                      <th scope="col">نوع</th>
                      <th scope="col">مقدار</th>
                      <th scope="col">
                        حداکثر مبلغ تخفیف <Currency />
                      </th>
                      <th scope="col">
                        حداقل مبلغ خرید <Currency />
                      </th>
                      <th scope="col">تعداد</th>
                      <th scope="col">تعداد استفاده</th>
                      <th scope="col">تاریخ ایجاد</th>
                      <th scope="col">وضعیت</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {discountCodes.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>{item.title}</td>
                        <td>{item.code}</td>
                        <td>{convertDiscountCodeTypeToText(item.type)}</td>
                        <td>
                          {item.type === discountCodeTypeItems[0] ? (
                            `%${numberSeparate(item.percentage)}`
                          ) : (
                            <>
                              <ConvertCurrency price={item.amount} />{" "}
                              <Currency />
                            </>
                          )}
                        </td>
                        <td>
                          {item.type === discountCodeTypeItems[0] ? (
                            item.maxDiscount == 0 ? (
                              "نامحدود"
                            ) : (
                              <ConvertCurrency price={item.maxDiscount} />
                            )
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <ConvertCurrency price={item.minPurchase} />
                        </td>
                        <td>
                          {item.limit == 0
                            ? "نامحدود"
                            : numberSeparate(item.limit)}
                        </td>
                        <td>{numberSeparate(item.uses)}</td>
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <Badge color={item.isActive ? "success" : "danger"}>
                            {item.isActive ? "فعال" : "غیرفعال"}
                          </Badge>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="hover-bg-transparent"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <Icon
                                icon="ph:dots-three-outline-fill"
                                className="menu-icon"
                              />
                            </button>
                            <ul className="dropdown-menu list">
                              <li
                                onClick={() => {
                                  reset();
                                  handleShowModal(item, setValue);
                                }}
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                              >
                                ویرایش
                              </li>

                              <li className="dropdown-divider" />

                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () =>
                                      handleChangeDiscountCodeActive(item._id),
                                    `آیا از ${
                                      !item.isActive ? "فعال" : "غیرفعال"
                                    } کردن کدتخفیف ${item.title} مطمئنید؟`
                                  )
                                }
                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-${
                                  !item.isActive ? "success" : "danger"
                                }`}
                              >
                                {!item.isActive ? "فعال" : "غیرفعال"}
                              </li>
                              <li
                                onClick={() =>
                                  areYouSureMessage(
                                    () => handleDeleteDiscountCode(item._id),
                                    `آیا از حذف کدتخفیف ${item.title} مطمئنید؟`
                                  )
                                }
                                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                              >
                                حذف
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(discountCode) ? "افزودن کدتخفیف جدید" : "ویرایش کدتخفیف"}
          </span>
        </ModalHeader>
        <ModalBody>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(discountCode)
                ? handleCreateDiscountCode
                : handleEditDiscountCode
            )}
          >
            <div className="row">
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="title"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      عنوان
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="title"
                      placeholder="عنوان را وارد نمایید"
                      {...register("title")}
                    />
                  </div>

                  <ErrorMessage message={errors.title?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="code"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      کد
                    </label>
                    <div className="input-group" dir="ltr">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() =>
                          setValue("code", generateRandomDiscountCode())
                        }
                      >
                        <Icon
                          icon="solar:refresh-circle-broken"
                          className="text-xxl"
                        />
                      </button>
                      <input
                        type="text"
                        className="form-control radius-8 text-end"
                        id="code"
                        placeholder="کد را وارد نمایید"
                        {...register("code")}
                      />
                    </div>
                  </div>

                  <ErrorMessage message={errors.code?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="type"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      نوع
                    </label>
                    <select
                      className="form-control radius-8"
                      {...register("type")}
                    >
                      <option value="" selected hidden>
                        نوع را انتخاب نمایید
                      </option>
                      {discountCodeTypeItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={watch("type") === item}
                        >
                          {convertDiscountCodeTypeToText(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <ErrorMessage message={errors.type?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="percentage"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      درصد
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          disabled={watch("type") !== discountCodeTypeItems[0]}
                          type="text"
                          placeholder="درصد را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="percentage"
                    />
                  </div>

                  <ErrorMessage message={errors.percentage?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="maxDiscount"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      حداکثر مبلغ تخفیف <Currency />
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          disabled={watch("type") !== discountCodeTypeItems[0]}
                          type="text"
                          placeholder="حداکثر مبلغ تخفیف را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="maxDiscount"
                    />
                    {watch("maxDiscount") == 0 ? (
                      <span>نامحدود</span>
                    ) : (
                      <ConvertCurrencyToText price={watch("maxDiscount")} />
                    )}
                  </div>

                  <ErrorMessage message={errors.maxDiscount?.message} />
                </div>
              </div>
              <div className="col-sm-6" key={discountCodeTypeItems[1]}>
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="amount"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      مبلغ <Currency />
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          disabled={watch("type") !== discountCodeTypeItems[1]}
                          type="text"
                          placeholder="مبلغ را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="amount"
                    />
                    <ConvertCurrencyToText price={watch("amount")} />
                  </div>

                  <ErrorMessage message={errors.amount?.message} />
                </div>
              </div>

              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="minPurchase"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      حداقل مبلغ خرید <Currency />
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          type="text"
                          placeholder="حداقل مبلغ خرید را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="minPurchase"
                    />
                    <ConvertCurrencyToText price={watch("minPurchase")} />
                  </div>

                  <ErrorMessage message={errors.minPurchase?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="limit"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      تعداد
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          type="text"
                          placeholder="تعداد را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="limit"
                    />
                    {watch("limit") == 0 ? (
                      <span>نامحدود</span>
                    ) : (
                      <ConvertCurrencyToText price={watch("limit")} />
                    )}
                  </div>

                  <ErrorMessage message={errors.limit?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="expiredAt"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      تاریخ انقضا
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <DateTimePicker
                          id="birthDate"
                          name="birthDate"
                          className="form-control radius-8"
                          placeholder="تاریخ انقضا را وارد نمایید"
                          onChange={onChange}
                          onBlur={onBlur}
                          initValue={value}
                        />
                      )}
                      name="expiredAt"
                    />
                  </div>

                  <ErrorMessage message={errors.expiredAt?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="singleUse"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      محدودیت استفاده کاربر
                    </label>
                    <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative">
                      <label
                        htmlFor="singleUse"
                        className="position-absolute w-100 h-100 start-0 top-0"
                      />
                      <div className="d-flex align-items-center gap-3 justify-content-between">
                        <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                          تک کاربره
                        </span>
                        <Controller
                          control={control}
                          render={({ field: { value, onChange, onBlur } }) => (
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="singleUse"
                              onChange={(e) => {
                                const value = e.target.checked;
                                onChange(value);
                              }}
                              onBlur={onBlur}
                              checked={value}
                            />
                          )}
                          name="singleUse"
                        />
                      </div>
                    </div>
                  </div>

                  <ErrorMessage message={errors.singleUse?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="users"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      کاربران
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          isMulti
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا کاربران را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>کاربری برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetUsers(val, callback);
                          }}
                          onChange={(e) => {
                            onChange(e);
                          }}
                          value={value}
                          isClearable
                        />
                      )}
                      name="users"
                    />
                  </div>

                  <ErrorMessage message={errors.users?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="products"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      محصولات
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          isMulti
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا محصولات را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>محصولی برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetProducts(val, callback);
                          }}
                          onChange={(e) => {
                            onChange(e);
                          }}
                          value={value}
                          isClearable
                        />
                      )}
                      name="products"
                    />
                  </div>

                  <ErrorMessage message={errors.products?.message} />
                </div>
              </div>
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="categories"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      دسته بندی ها
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          isMulti
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا دسته بندی ها را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>دسته بندی برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetCategories(val, callback);
                          }}
                          onChange={(e) => {
                            onChange(e);
                          }}
                          value={value}
                          isClearable
                        />
                      )}
                      name="categories"
                    />
                  </div>

                  <ErrorMessage message={errors.categories?.message} />
                </div>
              </div>
              <div className="col-sm-12">
                <div className="mb-20">
                  <label
                    htmlFor="description"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    توضیحات
                  </label>
                  <textarea
                    name="description"
                    className="form-control radius-8"
                    id="description"
                    rows={5}
                    placeholder="توضیحات ..."
                    {...register("description")}
                  />
                </div>
              </div>
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(discountCode) ? "افزودن" : "ویرایش"} کدتخفیف
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DiscountCodesAdmin;
